// frontend/src/components/SignIn.js

import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  CssBaseline,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Divider,
  Link,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  Brightness4,
  Brightness7,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { ThemeContext } from "../ThemeContext";
import API from "../API";

function SignIn() {
  const { darkMode, toggleTheme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [transitionActive, setTransitionActive] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      background: {
        default: darkMode ? "#0d1117" : "#f9f9f9",
      },
      text: {
        primary: darkMode ? "#c9d1d9" : "#000000",
        secondary: darkMode ? "#8b949e" : "#5f6368",
      },
    },
    typography: {
      fontFamily: "'Roboto', sans-serif",
    },
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email.trim() || !password.trim()) {
      setSnackbarMessage("Email и пароль не могут быть пустыми.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      // Отправляем данные через axios
      const params = new URLSearchParams();
      params.append("username", email);
      params.append("password", password);

      const response = await API.post("/auth/login", params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      localStorage.setItem("token", response.data.access_token);
      navigate("/Home_menu");

      // После удачного логина «досабмитим» скрытую форму,
      // чтобы браузер понял, что это логин и предложил сохранить пароль
      document.getElementById("hiddenLoginForm").submit();
    } catch (err) {
      setSnackbarMessage("Неверное имя пользователя или пароль");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
        }}
      >
        <IconButton onClick={toggleTheme} color="inherit">
          {darkMode ? <Brightness7 /> : <Brightness4 />}
        </IconButton>
      </Box>
      {/* Основная "видимая" форма (React-форма) */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <motion.div
          initial={{ width: "400px", height: "100vh" }}
          animate={
            transitionActive
              ? { width: "100vw", height: "100vh" }
              : { width: "400px", height: "100vh" }
          }
          transition={{ duration: 2.5 }}
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: darkMode ? "#161b22" : "#ffffff",
            boxShadow: darkMode
              ? "0 5px 20px rgba(0, 0, 0, 0.6)"
              : "0 5px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          {!transitionActive && (
            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: transitionActive ? 0 : 1 }}
              transition={{ duration: 2 }}
              style={{
                width: "100%",
                maxWidth: "350px",
                padding: "1.5rem",
                borderRadius: "15px",
                backgroundColor: darkMode ? "#161b22" : "#ffffff",
              }}
            >
              <Typography
                component="h1"
                variant="h5"
                align="center"
                sx={{
                  fontWeight: "bold",
                  marginBottom: "1rem",
                  color: theme.palette.text.primary,
                }}
              >
                Sign In
              </Typography>
              {/* Обратите внимание: "component='form'" + method + action + autoComplete */}
              <Box
                component="form"
                method="POST"
                action="/auth/login"
                autoComplete="on"
                noValidate
                onSubmit={handleSubmit}
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                <FormControl>
                  <FormLabel
                    htmlFor="email"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Email
                  </FormLabel>
                  <TextField
                    id="email"
                    type="email"
                    name="username"
                    autoComplete="email"
                    placeholder="your@email.com"
                    fullWidth
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                      input: {
                        backgroundColor: darkMode ? "#0d1117" : "#f9f9f9",
                        color: darkMode ? "#ffffff" : "#000000",
                        borderRadius: "15px",
                        padding: "10px",
                      },
                      ".MuiOutlinedInput-root": {
                        borderRadius: "15px",
                        backgroundColor: darkMode ? "#161b22" : "#ffffff",
                      },
                      fieldset: {
                        borderColor: darkMode ? "#8b949e" : "#cccccc",
                      },
                      "& .MuiOutlinedInput-root:hover fieldset": {
                        borderColor: "#58a6ff",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                        borderColor: "#58a6ff",
                      },
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel
                    htmlFor="password"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Password
                  </FormLabel>
                  <TextField
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    placeholder="••••••"
                    fullWidth
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePassword}
                            edge="end"
                            sx={{
                              backgroundColor: darkMode ? "#0d1117" : "#f9f9f9",
                              borderRadius: "50%",
                              padding: "8px",
                              "&:hover": {
                                backgroundColor: darkMode
                                  ? "#161b22"
                                  : "#e0e0e0",
                              },
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      input: {
                        backgroundColor: darkMode ? "#0d1117" : "#f9f9f9",
                        color: darkMode ? "#ffffff" : "#000000",
                        borderRadius: "15px",
                        padding: "10px",
                      },
                      ".MuiOutlinedInput-root": {
                        borderRadius: "15px",
                        backgroundColor: darkMode ? "#161b22" : "#ffffff",
                      },
                      fieldset: {
                        borderColor: darkMode ? "#8b949e" : "#cccccc",
                      },
                      "& .MuiOutlinedInput-root:hover fieldset": {
                        borderColor: "#58a6ff",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                        borderColor: "#58a6ff",
                      },
                    }}
                  />
                </FormControl>
                <FormControlLabel
                  control={<Checkbox color="primary" />}
                  label="Remember me"
                  sx={{
                    color: theme.palette.text.primary,
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: darkMode ? "#238636" : "#1976d2",
                    color: "#ffffff",
                    fontWeight: "bold",
                    padding: "10px",
                    borderRadius: "15px",
                    "&:hover": {
                      backgroundColor: darkMode ? "#2ea043" : "#1565c0",
                    },
                  }}
                >
                  Sign In
                </Button>
                <Link
                  href="#"
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    color: theme.palette.text.secondary,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Forgot your password?
                </Link>
              </Box>
              <Divider>or</Divider>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<GoogleIcon />}
                  sx={{
                    textTransform: "none",
                    borderColor: darkMode ? "#8b949e" : "#cccccc",
                    borderRadius: "15px",
                    padding: "10px",
                    "&:hover": {
                      borderColor: "#58a6ff",
                      backgroundColor: darkMode ? "#0d1117" : "#f0f0f0",
                    },
                  }}
                >
                  Sign in with Google
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FacebookIcon />}
                  sx={{
                    textTransform: "none",
                    borderColor: darkMode ? "#8b949e" : "#cccccc",
                    borderRadius: "15px",
                    padding: "10px",
                    "&:hover": {
                      borderColor: "#58a6ff",
                      backgroundColor: darkMode ? "#0d1117" : "#f0f0f0",
                    },
                  }}
                >
                  Sign in with Facebook
                </Button>
                <Typography
                  sx={{ textAlign: "center", color: theme.palette.text.secondary }}
                >
                  Don’t have an account?{" "}
                  <Link
                    href="/signup"
                    sx={{
                      color: darkMode ? "#58a6ff" : "#0066cc",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Sign up
                  </Link>
                </Typography>
              </Box>
            </motion.div>
          )}
        </motion.div>
      </Box>

      {/* Cкрытая форма, которую «досабмитим» после успешной авторизации */}
      <form
        id="hiddenLoginForm"
        method="POST"
        action="/auth/login"
        style={{ display: "none" }}
      >
        {/* Эти поля должны иметь те же name, что и на видимой форме */}
        <input type="text" name="username" value={email} readOnly />
        <input type="password" name="password" value={password} readOnly />
        <button type="submit">Hidden Submit</button>
      </form>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default SignIn;
